/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition, DataAxisProps } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import type { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { getLinearAxisValueFormatter } from '#utils/PRIVATE_chartUtils/numberFomatUtils';
import { ScaleLinear, ScaleLog } from '#utils/PRIVATE_chartUtils';
import { getAxisMaxSize, getAxisRange, getTickLabelAnchor } from '#PRIVATE_Axis/utils/axisUtils';
import { getTitleProps } from '#PRIVATE_Axis/utils/titleUtils';
import { AXIS_DEFAULTS } from '#PRIVATE_Axis/utils/axisDefaults';
import {
  getDataAxisLabelsInfo,
  getMaxHeight,
  getMaxWidth,
  getTickLabelInfo,
  updateLabelsDims
} from '#PRIVATE_Axis/utils/labelUtils';
import { AxisInfo } from '#PRIVATE_Axis/axis.types';

/**
 * Returns the maximum available height for the tick label, which is the
 * distance between two ticks.
 * @param ticks
 * @param scale
 * @returns
 */
export function getMaxAvailHeight(ticks: number[], scale: ScaleLinear | ScaleLog) {
  const range = scale.range();
  return Math.abs(range[1] - range[0]) / ticks.length;
}

/**
 * The function that returns the y axis properties.
 * @param dataAxisInfo The DataAxisInfo.
 * @param dataAxis The dataAxis props.
 * @param groups The chart groups.
 * @param position The position of axis in the given space.
 * @param availSpace The available space.
 * @param isRtl Whether the reading direction is right to left.
 * @param getTextDimensions Function for text measurement.
 * @returns
 */
export function getDataAxisProps(
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  locale: string,
  dataAxisInfo: AxisInfo,
  dataAxis?: DataAxisProps,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensionsType,
  isAxisRendered?: boolean
) {
  const [start, end] = getAxisRange(availSpace, position, isRtl);
  const scale = dataAxisInfo.getScale!([start, end]);

  if (dataAxis?.isRendered === false || isAxisRendered === false) {
    // if isRendered is undefined we render the axis.
    return {
      title: undefined,
      titleStyle: {},
      titleProps: undefined,
      axisDim: 0,
      tickLabels: [],
      tickLabelStyle: undefined,
      scale,
      formatYValue: undefined,
      groupWidth: 0,
      formatAxisLabel: undefined // TODO : JET-69997 - groupAxis support formatLabel
    };
  }

  const isHoriz = position === 'top' || position === 'bottom';
  const isSize = dataAxis?.size !== undefined;
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, dataAxis?.size, dataAxis?.maxSize);
  const titleStyle = { fontSize: axisFontSize, ...dataAxis?.titleStyle };
  const { titleDim, ...titleProps } = getTitleProps(
    dataAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    isRtl,
    getTextDimensions
  );
  const { width: titleWidth, height: titleHeight } = titleProps.dims!;
  const isTitleRendered = titleWidth > 0 && titleHeight > 0;
  const axisDim = isTitleRendered ? titleDim + AXIS_DEFAULTS.titleGap : 0;
  const isRotated = position === 'left' || position === 'right';

  if (isTitleRendered) {
    if (isRotated) {
      availSpace.width -= axisDim;
      availSpace.x += position === 'left' ? axisDim : 0;
    } else {
      availSpace.height -= axisDim;
      availSpace.y += position === 'top' ? axisDim : 0;
    }
  }

  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: dataAxis?.tickLabel?.style?.color,
    ...dataAxis?.tickLabel?.style,
    textAnchor: getTickLabelAnchor(position, isRtl)
  };
  const ticks = dataAxisInfo.ticks;

  const linearFormatter = getLinearAxisValueFormatter(
    ticks[0],
    ticks[ticks.length - 1],
    dataAxisInfo.step,
    locale
  );

  let isLabelRendered = dataAxis?.tickLabel?.isRendered != false;

  const labels = isLabelRendered
    ? ticks.map((i) => {
        if (dataAxis?.tickLabel?.format) {
          return dataAxis.tickLabel.format(i);
        }

        if (dataAxis?.scale === 'log') {
          const formatter = getLinearAxisValueFormatter(i, i, i, locale);
          return formatter(i);
        }
        return linearFormatter(i);
      })
    : [];

  const maxLabelHeight = axisMaxDim - axisDim - AXIS_DEFAULTS.labelGap;

  const getLabelInfo = (tick: string | undefined, index: number, _: boolean) => {
    const coord = scale.transform(ticks[index]);
    return getTickLabelInfo(
      coord,
      tick,
      index,
      tickLabelStyle,
      isHoriz,
      false,
      false,
      false,
      isHoriz ? maxLabelHeight : axisMaxDim - axisDim,
      isHoriz ? axisMaxDim - axisDim : maxLabelHeight,
      availSpace,
      getTextDimensions
    );
  };

  const { maxWidth, maxHeight, tickLabels } = getDataAxisLabelsInfo(labels, getLabelInfo, isHoriz);

  isLabelRendered =
    isLabelRendered && isHoriz
      ? maxHeight + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim
      : maxWidth + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim;

  const getAxisPos = (index: number) => {
    return scale.transform(ticks[index]);
  };

  let labelDims = 0;
  if (isLabelRendered) {
    const maxW = getMaxWidth(isSize, position, axisMaxDim, maxWidth, titleDim);
    const maxH = getMaxHeight(isSize, position, axisMaxDim, maxHeight, titleDim);
    updateLabelsDims(maxW, maxH, tickLabels, getAxisPos, availSpace, position);
    labelDims = isHoriz ? maxH + AXIS_DEFAULTS.labelGap : maxW + AXIS_DEFAULTS.labelGap;
  }

  const defaultLabelFormatter = (value: number) => {
    if (dataAxis?.scale === 'log') {
      const formatter = getLinearAxisValueFormatter(value, value, value, locale);
      return formatter(value);
    }
    return linearFormatter(value);
  };

  return {
    title: isTitleRendered ? dataAxis?.title : undefined,
    titleStyle,
    titleProps: isTitleRendered ? titleProps : undefined,
    axisDim: isTitleRendered ? labelDims + axisDim : labelDims,
    tickLabels: isLabelRendered ? tickLabels : [],
    tickLabelStyle,
    scale,
    defaultLabelFormatter,
    axisStepWidth: 0,
    averageGroupZ: 0,
    separators: [],
    isGroupLabelRendered: () => false
  };
}
